<script setup lang="ts">
/**
 * 布局级全屏 AI 对话：与右侧 AiAssistantPanel 共用会话 store，展示逻辑对齐侧栏（思考步骤、等待动画）。
 */
import { computed, inject, nextTick, onMounted, onUnmounted, ref, unref, watch } from 'vue';
import type { Ref } from 'vue';
import { storeToRefs } from 'pinia';
import { useRoute, useRouter } from 'vue-router';
import { ElMessage } from 'element-plus';
import type AiAssistantPanel from '@/components/AiAssistantPanel.vue';
import { useAiAssistantStore, type ChatMessage } from '@/stores/useAiAssistantStore';
import {
  SIDEBAR_NEW_CHAT_QUICK_PROMPTS,
  useAppShellStore,
  type ExpandedProjectDraftPayload,
} from '@/stores/useAppShellStore';
import { usePermissionStore } from '@/stores/usePermissionStore';
import { renderMarkdown } from '@/utils/markdown';
import { getMessageThoughtSteps, shouldShowInlineThought } from '@/utils/aiThought';
import {
  formatProjectCreateNoRouteAccessNotice,
  hasProjectCreateNoRouteAccessNotice,
  isStructuredProjectCreateReady,
  PROJECT_CREATE_NO_ACCESS_MESSAGE,
  stripCreateProjectGapNotice,
} from '@/utils/projectCreateReadiness';
import { canAccessProjectCreateRoute } from '@/utils/projectCreateRouteAccess';
import { runProjectAutofillIntroSequence } from '@/utils/projectAutofillTransition';
import {
  getProjectCreateUserRefFromMessage,
  isPendingProjectCreateFieldEditMessage,
  projectCreateFieldEditDoneLabel,
} from '@/utils/projectCreateMessagePayloads';
import {
  aiPageContentFieldEditDoneLabel,
  getAiPageContentUserRefFromMessage,
  isPendingAiPageContentFieldEditMessage,
  resolveAiPageContentLocateOptions,
  resolveAssistantMessageDisplayContent,
} from '@/utils/aiPageContentMessagePayloads';
import { getAgentConversationList, renameAgentConversation, type ConversationSummary } from '@/api/agentApi';
import { uploadChatFile } from '@/api/fileApi';
import { ensureUploadLimits } from '@/api/uploadLimitsApi';
import type { ChatMessageAttachment } from '@/stores/useAiAssistantStore';
import { useAiSessionNotifyStore } from '@/stores/useAiSessionNotifyStore';
import { saveProjectDetailEngineeringTab } from '@/utils/projectDetailTabState';
import { useAiTaskQueueStore } from '@/stores/useAiTaskQueueStore';
import { clampSessionTitle } from '@/utils/aiSessionTitle';
import { resolveAiConversationScenario } from '@/utils/aiSessionContext';
import { isProjectWbsGenerateLocked } from '@/utils/projectWbsGenerateLock';
import { wbsManualEditEpoch } from '@/utils/wbsGenerateSession';
import { isIgnorableAuthError } from '@/api/http';
import { useAuthStore } from '@/stores/useAuthStore';
import { getUserSessionScopeSuffix } from '@/utils/userSessionScope';
import {
  CHAT_ATTACHMENT_ACCEPT,
  CHAT_ATTACHMENT_HINT,
  isChatImageFile,
  validateChatAttachmentFiles,
} from '@/utils/chatAttachmentRules';

const DEFAULT_AI_SCENARIO = 'project_assistance';

const route = useRoute();
const router = useRouter();
const shellStore = useAppShellStore();
const aiAssistantStore = useAiAssistantStore();
const authStore = useAuthStore();
const sessionNotify = useAiSessionNotifyStore();
const taskQueue = useAiTaskQueueStore();
const permissionStore = usePermissionStore();
const { assistantTypingPreviewByMessageId } = storeToRefs(aiAssistantStore);
const aiPanelRef = inject<Ref<InstanceType<typeof AiAssistantPanel> | null>>('aipdAiPanelRef', ref(null));

/** 与 AiAssistantPanel.conversationScenario 一致，避免展开态与侧栏会话错位 */
const COMPONENT_SCENARIO = 'component_library';
const PROJECT_ROUTE_NAMES = new Set(['ProjectDashboard', 'ProjectRequirements']);
const hasExpandedProjectContext = computed(
  () =>
    PROJECT_ROUTE_NAMES.has(String(route.name ?? '')) ||
    (typeof route.path === 'string' && /^\/project\/[^/]+\//.test(route.path)),
);
const expandedProjectId = computed(() => {
  if (!hasExpandedProjectContext.value) return 0;
  const rawId = route.params.id ?? route.params.projectId ?? route.params.pid;
  if (rawId == null || rawId === '') return 0;
  const num = Array.isArray(rawId) ? Number(rawId[0]) : Number(rawId);
  return Number.isFinite(num) && num > 0 ? num : 0;
});
const isComponentLibraryRoute = computed(
  () => route.name === 'Resources' || route.name === 'ResourceCreate' || route.name === 'ResourceDetail',
);

/** 与 Layout / PrimarySidebar 对齐：顶栏高度、主内容区左缘（不盖住顶栏与左侧导航） */
const APP_HEADER_HEIGHT_PX = 56;
const primaryRailWidthPx = computed(() =>
  shellStore.sidebarCollapsed ? shellStore.PRIMARY_RAIL_COLLAPSED_WIDTH : shellStore.PRIMARY_RAIL_WIDTH,
);
const expandedShellRectStyle = computed(() => ({
  top: `${APP_HEADER_HEIGHT_PX}px`,
  left: `${primaryRailWidthPx.value}px`,
  right: '0px',
  bottom: '0px',
}));

const expandedAiInput = ref('');
const expandedComposerScrollRef = ref<HTMLElement | null>(null);
const expandedFileInputRef = ref<HTMLInputElement | null>(null);
const expandedEditingSessionTitle = ref(false);
const expandedEditingSessionTitleDraft = ref('');
const expandedSessionTitleInputRef = ref<HTMLInputElement | null>(null);
const expandedDragging = ref(false);
const expandedDragDepth = ref(0);
/** 发送后立即清空输入区，此后等待状态以侧栏 isThinking / aiLoading 为准 */
const expandedSending = ref(false);
const expandedImageBase64 = ref('');
const expandedImagePreview = ref('');
const expandedThoughtIds = ref(new Set<string>());
/** 展开态内 Tab：历史列表嵌在 overlay 内，不退出展开形态 */
const expandedMainTab = ref<'new' | 'history'>('new');
const expandedHistoryList = ref<ConversationSummary[]>([]);
const expandedHistoryLoading = ref(false);
const expandedHighlightSessionId = ref('');
const expandedEditingHistoryId = ref<number | null>(null);
let expandedHistoryLoadGen = 0;
const expandedEditingHistoryTitle = ref('');
const expandedHistoryTitleInputRef = ref<HTMLInputElement | null>(null);
const expandedPcRefOpenId = ref<string | null>(null);

interface ExpandedPendingAttachment {
  id: string;
  name: string;
  file: File;
  url: string;
}

const expandedAttachments = ref<ExpandedPendingAttachment[]>([]);

const DASHBOARD_PAGE_QUICK_TAGS: { label: string; text: string }[] = [
  { label: '@创建项目', text: '@创建项目\n请按三步确认，明确确认前不要落库：\n1）基础信息（名称、客户、总体交付日期、负责人）\n2）成员（未匹配组织目录的只标记，不新建账号，可跳过）\n3）补充配置（阶段计划/任务模板）\n全部确认后再创建，完成后提供「查看项目配置」。' },
  { label: '查找项目', text: '请根据当前筛选条件，帮我查找需要重点关注的几个项目，并简单说明原因。' },
  { label: '进度异常项目', text: '请帮我找出进度明显滞后的项目，并给出可能的原因假设和建议措施。' },
  { label: '高风险项目', text: '请罗列当前高风险项目，并按紧急程度排序，给出对应的处理建议。' },
];

const expandedQuickTags = computed(() => {
  const pageTags = canAccessProjectCreateRoute(permissionStore)
    ? DASHBOARD_PAGE_QUICK_TAGS
    : DASHBOARD_PAGE_QUICK_TAGS.filter((t) => t.label !== '@创建项目');
  return [
    ...SIDEBAR_NEW_CHAT_QUICK_PROMPTS.map((t) => ({ label: t.label, text: t.text })),
    ...pageTags,
  ];
});

const expandedConversationScenario = computed(() => {
  if (isComponentLibraryRoute.value) {
    return COMPONENT_SCENARIO;
  }
  return resolveAiConversationScenario(route, aiAssistantStore.currentScenario || DEFAULT_AI_SCENARIO);
});

function isExpandedHistorySessionRunning(sessionId: string): boolean {
  return taskQueue.sessionHasQueuedOrRunning(expandedConversationScenario.value, sessionId);
}

function isExpandedHistorySessionUnread(item: ConversationSummary): boolean {
  return sessionNotify.isUnread(expandedConversationScenario.value, item.sessionId);
}

const expandedHistoryHasUnread = computed(() =>
  sessionNotify.hasUnreadInScenario(expandedConversationScenario.value),
);

const expandedActiveQueueTasks = computed(() =>
  taskQueue.activeTasksForScope(expandedConversationScenario.value),
);

const expandedRunningTask = computed(() =>
  taskQueue.runningTaskForScope(expandedConversationScenario.value),
);
const expandedPausedTask = computed(() =>
  taskQueue.pausedTaskForScope(expandedConversationScenario.value),
);
const expandedQueueHeld = computed(() =>
  taskQueue.isScopeQueueHeld(expandedConversationScenario.value),
);
const expandedShowTaskStop = computed(() => {
  const inst = aiPanelRef.value as { showComposerTaskStop?: boolean | { value: boolean } } | null;
  const v = inst?.showComposerTaskStop;
  if (typeof v === 'boolean') return v;
  if (v && typeof v === 'object' && 'value' in v) return !!v.value;
  return !!expandedRunningTask.value && !expandedStreaming.value;
});

const expandedStreaming = computed(() => {
  const inst = aiPanelRef.value as { isStreaming?: { value: boolean } } | null;
  return !!inst?.isStreaming?.value;
});

function stopExpandedStream() {
  void onExpandedComposerStopClick();
}

function callPanelTaskAction(
  method:
    | 'onPauseScopeTask'
    | 'onStopScopeTask'
    | 'onResumeScopeTask'
    | 'onContinueScopeQueue',
) {
  const inst = aiPanelRef.value as Record<string, (() => void) | undefined> | null;
  inst?.[method]?.();
}

function onExpandedPauseScopeTask() {
  void onExpandedComposerStopClick();
}

async function onExpandedStopScopeTask() {
  await onExpandedComposerStopClick();
}

async function onExpandedComposerStopClick() {
  const inst = aiPanelRef.value as {
    onStopScopeTask?: () => void | Promise<void>;
    consumeRestoredComposerDraft?: () => string | null;
  } | null;
  await inst?.onStopScopeTask?.();
  const draft = inst?.consumeRestoredComposerDraft?.();
  if (typeof draft === 'string') {
    expandedAiInput.value = draft;
  }
}

function onExpandedResumeScopeTask() {
  callPanelTaskAction('onResumeScopeTask');
}

function onExpandedContinueScopeQueue() {
  callPanelTaskAction('onContinueScopeQueue');
}

function onExpandedCancelQueueTask(taskId: string) {
  const inst = aiPanelRef.value as { onCancelQueueTask?: (id: string) => void } | null;
  if (inst?.onCancelQueueTask) {
    inst.onCancelQueueTask(taskId);
    return;
  }
  taskQueue.cancelTask(taskId);
}
const expandedChatMessages = computed(() => {
  const sid = aiPanelRef.value?.getCurrentSessionId?.() ?? '';
  if (sid) {
    return aiAssistantStore.getMessages(expandedConversationScenario.value, sid);
  }
  return aiAssistantStore.getMessages(expandedConversationScenario.value);
});
const hasExpandedConversation = computed(() => expandedChatMessages.value.length > 0);

const isDashboardRoute = computed(() => route.name === 'Dashboard');

/** 与侧栏 ai-title 对齐（随场景 / 路由变化刷新） */
const expandedSessionTitle = computed(() => {
  void expandedConversationScenario.value;
  const inst = aiPanelRef.value as { getCurrentSessionTitle?: () => string } | null;
  const t = inst?.getCurrentSessionTitle?.();
  if (t && String(t).trim()) return t;
  return '通用会话';
});

const panelIsThinking = computed(() => {
  const inst = aiPanelRef.value as { isThinking?: Ref<boolean> } | null;
  return inst?.isThinking ? unref(inst.isThinking) : false;
});

const panelLiveThoughtSteps = computed(() => {
  const inst = aiPanelRef.value as { liveThoughtSteps?: Ref<Array<{ key: string; icon: string; title: string; text: string }>> } | null;
  return inst?.liveThoughtSteps ? unref(inst.liveThoughtSteps) : [];
});

const panelAiLoading = computed(() => {
  const inst = aiPanelRef.value as { aiLoading?: Ref<boolean> } | null;
  return inst?.aiLoading ? unref(inst.aiLoading) : false;
});

type PageContentEditPendingView = {
  fieldPath: string;
  fieldLabel: string;
  quoted: string;
  positionDescription: string;
  scopeLabel: string;
  cellKey?: string;
  paragraphIndex?: number;
  paragraphLabel?: string;
  mode: 'modify' | 'discuss';
};

const panelPageContentEditPending = computed((): PageContentEditPendingView | null => {
  const inst = aiPanelRef.value as { getPageContentEditPending?: () => PageContentEditPendingView | null } | null;
  return inst?.getPageContentEditPending?.() ?? null;
});

function locateExpandedPageContentQuote(
  quoted: string,
  fieldPath: string,
  options?: { paragraphIndex?: number; paragraphLabel?: string; cellKey?: string },
) {
  const inst = aiPanelRef.value as {
    locatePageContentQuote?: (
      quoted: string,
      fieldPath: string,
      options?: { paragraphIndex?: number; paragraphLabel?: string; cellKey?: string },
    ) => boolean;
  } | null;
  return inst?.locatePageContentQuote?.(quoted, fieldPath, options) ?? false;
}

function onExpandedPendingRefChipClick() {
  const pending = panelPageContentEditPending.value;
  if (!pending) return;
  const ok = locateExpandedPageContentQuote(pending.quoted, pending.fieldPath, {
    paragraphIndex: pending.paragraphIndex,
    paragraphLabel: pending.paragraphLabel,
    cellKey: pending.cellKey,
  });
  if (!ok) ElMessage.info('未在页面中定位到引用原文，可能内容已变更');
}

function clearExpandedPageContentRef() {
  const inst = aiPanelRef.value as { clearPageContentFieldRef?: () => void } | null;
  inst?.clearPageContentFieldRef?.();
}

/** 最后一条助手消息已有可见正文或打字片段时，不再显示「正在生成回复」 */
const lastAssistantHasVisibleExpandedReply = computed(() => {
  const messages = expandedChatMessages.value;
  if (!messages.length) return false;
  const last = messages[messages.length - 1];
  if (last.role !== 'assistant') return false;
  const previewMap = assistantTypingPreviewByMessageId.value;
  if (last.id in previewMap && String(previewMap[last.id] ?? '').length > 0) return true;
  return Boolean(last.content && String(last.content).trim().length > 0);
});

const expandedLastAssistantId = computed(() => {
  const list = expandedChatMessages.value;
  for (let i = list.length - 1; i >= 0; i -= 1) {
    if (list[i]?.role === 'assistant') return list[i].id;
  }
  return '';
});

function shouldShowExpandedInlineThought(msg: ChatMessage): boolean {
  return shouldShowInlineThought(msg, {
    visible: panelIsThinking.value,
    stepCount: panelLiveThoughtSteps.value.length,
    ownerMessageId: expandedLastAssistantId.value,
  });
}

const showExpandedThinkingOverlay = computed(() => {
  if (lastAssistantHasVisibleExpandedReply.value) return false;
  if (panelLiveThoughtSteps.value.length > 0) return false;
  return expandedSending.value || panelIsThinking.value || panelAiLoading.value;
});

type ProjectDraft = ExpandedProjectDraftPayload;

function normalizeDraftValue(value: unknown) {
  if (value == null) return '';
  return String(value).trim();
}

function extractProjectDraftFromStructured(result: unknown): ProjectDraft | null {
  if (!result || typeof result !== 'object') return null;
  const source = result as Record<string, unknown>;
  const draft: ProjectDraft = {};
  const nameCandidates = [source.name, source.projectName, source.project_name, source.title, source.projectTitle];
  const codeCandidates = [source.code, source.projectCode, source.project_code, source.projectNumber, source.projectNo];
  const ownerCandidates = [source.owner, source.projectOwner, source.project_owner, source.customerName, source.customer];
  const deliveryCandidates = [source.deliveryDate, source.projectDeliveryDate, source.project_delivery_date, source.deadline, source.dueDate];
  const leadCandidates = [source.leadUserId, source.projectLeadUserId, source.project_lead_user_id, source.leadUserIdStr];
  draft.name = normalizeDraftValue(nameCandidates.find((v) => normalizeDraftValue(v))) || undefined;
  draft.code = normalizeDraftValue(codeCandidates.find((v) => normalizeDraftValue(v))) || undefined;
  draft.owner = normalizeDraftValue(ownerCandidates.find((v) => normalizeDraftValue(v))) || undefined;
  draft.deliveryDate = normalizeDraftValue(deliveryCandidates.find((v) => normalizeDraftValue(v))) || undefined;
  draft.leadUserId = normalizeDraftValue(leadCandidates.find((v) => normalizeDraftValue(v))) || undefined;
  return Object.values(draft).some(Boolean) ? draft : null;
}

function isAgentCompleteResult(sr: unknown): sr is { kind: string; route: string; params: Record<string, string>; label: string } {
  return !!(sr && typeof sr === 'object' && (sr as Record<string, unknown>).kind === 'agent_complete');
}

function getAgentCompleteNav(sr: unknown): { route: string; params: Record<string, string>; label: string } | null {
  if (!isAgentCompleteResult(sr)) return null;
  const s = sr as Record<string, unknown>;
  return {
    route: String(s.route || ''),
    params: (s.params as Record<string, string>) || {},
    label: String(s.label || '查看结果'),
  };
}


function handleAgentNavClick(msg: any) {
  const sr = msg?.structuredResult ?? null;
  if (!sr || sr.kind !== 'agent_complete') return;
  const nav = getAgentCompleteNav(sr);
  if (nav) navigateAgentComplete(nav);
}

function navigateAgentComplete(nav: { route: string; params: Record<string, string> }) {
  const id = nav.params.id;
  const tab = nav.params.tab;
  // Save tab state before navigation
  if (tab && id) {
    try {
      saveProjectDetailEngineeringTab(Number(id), tab as 'bom' | 'components');
    } catch (e) { /* ignore */ }
  }
  const routeNameMap: Record<string, string> = {
    '/project/[id]/dashboard': 'ProjectDashboard',
    '/project/[id]/requirements': 'ProjectRequirements',
    '/project/[id]/wbs': 'ProjectWbsSnapshot',
  };
  const name = routeNameMap[nav.route];
  if (name && id) {
    // Check if already on the target page
    const currentRoute = router.currentRoute.value;
    if (currentRoute.name === name && String(currentRoute.params.id) === String(id)) {
      // Already here — reload to apply saved tab state
      window.location.reload();
    } else {
      router.push({ name, params: { id } });
    }
  } else if (nav.route === '/ai-review') {
    if (router.currentRoute.value.name === 'AiSmartReview') {
      window.location.reload();
    } else {
      router.push({ name: 'AiSmartReview' });
    }
  } else {
    let resolved = nav.route;
    for (const [key, value] of Object.entries(nav.params)) {
      resolved = resolved.replace('[' + key + ']', String(value));
    }
    router.push(resolved);
  }
}

function getExpandedMessageStructuredResult(msg: any) {
  return msg?.structuredResult ?? null;
}

function isExpandedWbsGenerateMessage(msg: any) {
  const sr = getExpandedMessageStructuredResult(msg);
  if (!sr || typeof sr !== 'object') return false;
  return (sr as { kind?: string }).kind === 'wbs_generate';
}

function expandedWbsGenerateStatus(msg: any) {
  const sr = getExpandedMessageStructuredResult(msg) as { status?: string } | null;
  return String(sr?.status || '');
}

function expandedWbsGenerateActionsVisible(msg: any) {
  if (!isExpandedWbsGenerateMessage(msg)) return false;
  const status = expandedWbsGenerateStatus(msg);
  return status === 'completed' || status === 'failed' || status === 'reverted';
}

function isExpandedWbsGenerateBusy(msg: any) {
  const sr = getExpandedMessageStructuredResult(msg) as { projectId?: number } | null;
  const pid = Number(sr?.projectId);
  if (!(Number.isFinite(pid) && pid > 0)) return false;
  return isProjectWbsGenerateLocked(pid);
}

function canExpandedRollbackWbs(msg: any) {
  void wbsManualEditEpoch.value;
  const inst = aiPanelRef.value as {
    canRollbackWbsFromMessage?: (m: any) => boolean;
  } | null;
  return !!inst?.canRollbackWbsFromMessage?.(msg);
}

function expandedWbsRollbackTitle(msg: any) {
  void wbsManualEditEpoch.value;
  const inst = aiPanelRef.value as {
    wbsRollbackDisabledTitle?: (m: any) => string;
  } | null;
  return inst?.wbsRollbackDisabledTitle?.(msg) || '';
}

function expandedSorRequirementActionsVisible(msg: any) {
  const inst = aiPanelRef.value as {
    sorRequirementActionsVisible?: (m: any) => boolean;
  } | null;
  return !!inst?.sorRequirementActionsVisible?.(msg);
}

function expandedSessionActionMessageActionsVisible(msg: any) {
  const inst = aiPanelRef.value as {
    sessionActionMessageActionsVisible?: (m: any) => boolean;
  } | null;
  return !!inst?.sessionActionMessageActionsVisible?.(msg);
}

function expandedIsWbsTaskReassignConfirm(msg: any) {
  const inst = aiPanelRef.value as {
    isWbsTaskReassignConfirm?: (m: any) => boolean;
  } | null;
  return !!inst?.isWbsTaskReassignConfirm?.(msg);
}

function getLatestAssistantStructuredResult() {
  const list = expandedChatMessages.value;
  const lastAssistant = [...list].reverse().find((msg) => msg.role === 'assistant');
  return lastAssistant?.structuredResult ?? null;
}

function hasEnoughProjectDraft(draft: ProjectDraft | null) {
  return Boolean(draft?.name && draft.code && draft.owner && draft.deliveryDate);
}

function getPanelSessionId(): string {
  const inst = aiPanelRef.value as { getCurrentSessionId?: () => string } | null;
  return inst?.getCurrentSessionId?.() ?? '';
}

function fillExpandedPrompt(text: string) {
  expandedAiInput.value = text;
}

function addExpandedAttachments(files: File[]) {
  const msg = validateChatAttachmentFiles(files, {
    total: expandedAttachments.value.length,
    images: expandedAttachments.value.filter((item) => isChatImageFile(item.file)).length,
  });
  if (msg) {
    ElMessage.warning(msg);
    return;
  }
  files.forEach((file) => {
    const id = `${Date.now()}_${Math.random().toString(36).slice(2, 8)}`;
    expandedAttachments.value.push({ id, name: file.name, file, url: URL.createObjectURL(file) });
  });
}

function removeExpandedAttachment(id: string) {
  const item = expandedAttachments.value.find((a) => a.id === id);
  if (item?.url && item.url.startsWith('blob:')) URL.revokeObjectURL(item.url);
  if (item?.id?.startsWith('paste_')) { expandedImageBase64.value = ''; (window as any).__aipdPendingImage = null; }
  expandedAttachments.value = expandedAttachments.value.filter((a) => a.id !== id);
}

function onExpandedFileSelect(e: Event) {
  const input = e.target as HTMLInputElement;
  const files = input.files;
  if (!files?.length) return;
  addExpandedAttachments(Array.from(files));
  input.value = '';
}

function expandedDataTransferHasFiles(e: DragEvent): boolean {
  const types = e.dataTransfer?.types;
  if (!types) return false;
  return Array.from(types).includes('Files');
}

function onExpandedDragEnter(e: DragEvent) {
  if (!expandedDataTransferHasFiles(e)) return;
  expandedDragDepth.value += 1;
  expandedDragging.value = true;
}

function onExpandedDragOver(e: DragEvent) {
  if (!expandedDataTransferHasFiles(e)) return;
  e.preventDefault();
  expandedDragging.value = true;
}

function onExpandedDrop(e: DragEvent) {
  expandedDragDepth.value = 0;
  expandedDragging.value = false;
  const files = e.dataTransfer?.files;
  if (!files?.length) return;
  addExpandedAttachments(Array.from(files));
}

function onExpandedDragLeave(e: DragEvent) {
  const shell = e.currentTarget as HTMLElement;
  const related = e.relatedTarget as Node | null;
  if (related && shell.contains(related)) return;
  expandedDragDepth.value = Math.max(0, expandedDragDepth.value - 1);
  if (expandedDragDepth.value === 0) expandedDragging.value = false;
}

function expandedMarkdown(html: string) {
  return renderMarkdown(html);
}

/** 助手消息：打字机动画期间用 store 中的片段渲染，与侧栏一致 */
function assistantExpandedBodyHtml(msg: ChatMessage) {
  if (msg.role !== 'assistant') return '';
  const map = assistantTypingPreviewByMessageId.value;
  const raw = msg.id in map
    ? (map[msg.id] ?? '')
    : resolveAssistantMessageDisplayContent(msg);
  return expandedMarkdown(stripCreateProjectGapNotice(raw));
}

function getExpandedUserCopyText(msg: ChatMessage): string {
  const pageRef = getAiPageContentUserRefFromMessage(msg);
  if (pageRef) {
    const tail = pageRef.requirement.trim() || '（按上下文润色）';
    return `[${pageRef.scopeLabel || '页面'} · ${pageRef.fieldLabel}] ${pageRef.quoted}\n\n${tail}`;
  }
  const ref = getProjectCreateUserRefFromMessage(msg);
  if (ref) {
    const tail = ref.requirement.trim() || '（按上下文润色）';
    return `[创建项目 · ${ref.fieldLabel}] ${ref.quoted}\n\n${tail}`;
  }
  return msg.content?.trim() ?? '';
}

function truncatePcRefSnippetExpanded(text: string, maxLen = 56): string {
  const s = String(text ?? '').replace(/\s+/g, ' ').trim();
  if (s.length <= maxLen) return s;
  return `${s.slice(0, maxLen)}…`;
}

function toggleExpandedPcRef(messageId: string) {
  expandedPcRefOpenId.value = expandedPcRefOpenId.value === messageId ? null : messageId;
}

function onExpandedPageContentUserRefClick(msg: ChatMessage) {
  const ref = getAiPageContentUserRefFromMessage(msg);
  if (!ref) return;
  const locateOpts = resolveAiPageContentLocateOptions(ref, expandedChatMessages.value, msg.id);
  const ok = locateExpandedPageContentQuote(ref.quoted, ref.fieldPath || '', locateOpts);
  toggleExpandedPcRef(msg.id);
  if (!ok && expandedPcRefOpenId.value === msg.id) {
    ElMessage.info('未在页面中定位到引用原文，已展开查看完整内容');
  }
}

async function copyExpandedMessage(text: string) {
  if (!text) return;
  try {
    await navigator.clipboard.writeText(text);
    ElMessage.success('已复制到剪贴板');
  } catch {
    ElMessage.error('复制失败');
  }
}

function removeExpandedUserMessage(messageId: string) {
  aiPanelRef.value?.removeUserMessage?.(messageId);
}

async function loadExpandedHistory() {
  const gen = ++expandedHistoryLoadGen;
  const scenario = expandedConversationScenario.value;
  const authScope = getUserSessionScopeSuffix();
  if (!authStore.isAuthenticated || authStore.loggingOut) {
    expandedHistoryList.value = [];
    expandedHistoryLoading.value = false;
    return;
  }
  expandedHistoryLoading.value = true;
  try {
    const list = await getAgentConversationList(scenario);
    if (
      gen !== expandedHistoryLoadGen
      || expandedConversationScenario.value !== scenario
      || authScope !== getUserSessionScopeSuffix()
      || !authStore.isAuthenticated
      || authStore.loggingOut
    ) return;
    expandedHistoryList.value = list;
  } catch (e) {
    if (
      gen !== expandedHistoryLoadGen
      || expandedConversationScenario.value !== scenario
      || authScope !== getUserSessionScopeSuffix()
      || !authStore.isAuthenticated
      || authStore.loggingOut
      || isIgnorableAuthError(e)
    ) return;
    expandedHistoryList.value = [];
  } finally {
    if (gen === expandedHistoryLoadGen) {
      expandedHistoryLoading.value = false;
    }
  }
}

async function openHistoryAiPanel() {
  expandedMainTab.value = 'history';
  aiPanelRef.value?.setConversationTab?.('history');
  await nextTick();
  expandedHighlightSessionId.value = getPanelSessionId();
}

function openExpandedCurrentConversation() {
  expandedMainTab.value = 'new';
  aiPanelRef.value?.setConversationTab?.('new');
  aiPanelRef.value?.expandRightPanel?.();
  void nextTick();
}

async function onExpandedHistoryRowClick(item: ConversationSummary) {
  await aiPanelRef.value?.loadHistoryItem?.(item);
  expandedMainTab.value = 'new';
  expandedHighlightSessionId.value = getPanelSessionId();
}

function startExpandedHistoryRename(item: ConversationSummary) {
  expandedEditingHistoryId.value = item.id;
  expandedEditingHistoryTitle.value = (item.title || '对话').trim();
  void nextTick(() => {
    const el = expandedHistoryTitleInputRef.value;
    if (!el) return;
    el.focus();
    el.select();
    const len = el.value.length;
    el.setSelectionRange(len, len);
  });
}

function cancelExpandedHistoryRename() {
  expandedEditingHistoryId.value = null;
  expandedEditingHistoryTitle.value = '';
}

async function confirmExpandedHistoryRename(item: ConversationSummary) {
  const current = (item.title || '对话').trim();
  const nextTitle = expandedEditingHistoryTitle.value.trim();
  if (!nextTitle || nextTitle === current) {
    cancelExpandedHistoryRename();
    return;
  }
  try {
    await renameAgentConversation({ id: item.id, title: nextTitle });
    await loadExpandedHistory();
    await aiPanelRef.value?.loadConversationHistory?.();
  } catch {
    ElMessage.error('重命名会话失败');
  } finally {
    cancelExpandedHistoryRename();
  }
}

async function confirmExpandedHistoryDelete(item: ConversationSummary) {
  const ok = await aiPanelRef.value?.confirmDeleteHistoryItem?.(item);
  if (ok) {
    await loadExpandedHistory();
  }
}

function toggleExpandedThought(messageId: string) {
  const next = new Set(expandedThoughtIds.value);
  if (next.has(messageId)) next.delete(messageId);
  else next.add(messageId);
  expandedThoughtIds.value = next;
}

function exitExpandedAiComposer() {
  shellStore.setDashboardExpandedAiComposer(false);
  expandedDragging.value = false;
  expandedMainTab.value = 'new';
  void nextTick(() => {
    const panel = aiPanelRef.value as { expandRightPanel?: () => void } | null;
    panel?.expandRightPanel?.();
  });
}

async function onExpandedNewSession() {
  const panel = aiPanelRef.value as { handleNewConversation?: () => Promise<void> } | null;
  await panel?.handleNewConversation?.();
  expandedMainTab.value = 'new';
  expandedHighlightSessionId.value = getPanelSessionId();
  await loadExpandedHistory();
}

/** 退出全屏并收起侧栏为右上角悬浮球 */
function onExpandedCollapseToFloat() {
  shellStore.setDashboardExpandedAiComposer(false);
  expandedDragging.value = false;
  expandedMainTab.value = 'new';
  void nextTick(() => {
    const panel = aiPanelRef.value as { collapseToFloatingButton?: () => void } | null;
    panel?.collapseToFloatingButton?.();
  });
}

function openDashboardProjectForm() {
  if (!canAccessProjectCreateRoute(permissionStore)) {
    ElMessage.warning(PROJECT_CREATE_NO_ACCESS_MESSAGE);
    return;
  }
  shellStore.setDashboardExpandedAiComposer(false);
  expandedDragging.value = false;
  shellStore.requestOpenDashboardProjectForm();
}

function onExpandedSessionTitleDblClick() {
  expandedEditingSessionTitleDraft.value = expandedSessionTitle.value;
  expandedEditingSessionTitle.value = true;
  void nextTick(() => {
    const el = expandedSessionTitleInputRef.value;
    el?.focus();
    el?.select();
  });
}

function cancelExpandedSessionTitleEdit() {
  expandedEditingSessionTitle.value = false;
  expandedEditingSessionTitleDraft.value = '';
}

async function confirmExpandedSessionTitleEdit() {
  if (!expandedEditingSessionTitle.value) return;
  const current = expandedSessionTitle.value.trim();
  const nextTitle = expandedEditingSessionTitleDraft.value.trim();
  cancelExpandedSessionTitleEdit();
  if (!nextTitle || nextTitle === current) return;
  const panel = aiPanelRef.value as { renameCurrentSessionTitle?: (t: string) => Promise<void> } | null;
  if (panel?.renameCurrentSessionTitle) {
    await panel.renameCurrentSessionTitle(clampSessionTitle(nextTitle));
    return;
  }
  ElMessage.warning('无法修改标题，请回到侧栏重试');
}

onMounted(() => {
  document.body.style.overflow = 'hidden';
  void ensureUploadLimits();
});

onUnmounted(() => {
  expandedHistoryLoadGen += 1;
  document.body.style.overflow = '';
});

watch(
  [expandedChatMessages, assistantTypingPreviewByMessageId, panelLiveThoughtSteps, panelIsThinking],
  async () => {
    await nextTick();
    const el = expandedComposerScrollRef.value;
    if (!el) return;
    el.scrollTop = el.scrollHeight;
    const clips = el.querySelectorAll('.ai-message-thought--live .ai-thought-preview-clip');
    clips.forEach((clip) => {
      const node = clip as HTMLElement;
      node.scrollTop = node.scrollHeight;
    });
  },
  { deep: true },
);

watch(
  [expandedMainTab, expandedConversationScenario],
  async ([tab]) => {
    if (tab === 'history') {
      await loadExpandedHistory();
      expandedHighlightSessionId.value = getPanelSessionId();
    }
  },
);

watch(panelPageContentEditPending, (pending, prev) => {
  if (pending && !prev) {
    expandedAiInput.value = '';
  }
});

async function onExpandedPaste(e: ClipboardEvent) {
  const items = e.clipboardData?.items;
  if (!items) return;
  for (let i = 0; i < items.length; i++) {
    const item = items[i];
    if (item.type.startsWith('image/')) {
      e.preventDefault();
      const file = item.getAsFile();
      if (!file) continue;
      const msg = validateChatAttachmentFiles([file], {
        total: expandedAttachments.value.length,
        images: expandedAttachments.value.filter((attachment) => isChatImageFile(attachment.file)).length,
      });
      if (msg) {
        ElMessage.warning(msg);
        return;
      }
      const reader = new FileReader();
      reader.onload = () => {
        const b64 = (reader.result as string).split(',')[1] || (reader.result as string);
        expandedImageBase64.value = b64;
        const url2 = reader.result as string;
        expandedAttachments.value.push({ id: 'paste_' + Date.now(), name: file.name, file, url: url2 });
      };
      reader.readAsDataURL(file);
      return;
    }
  }
}

async function sendExpandedAi() {
  const text = expandedAiInput.value.trim();
  const hasPendingRef = !!panelPageContentEditPending.value;
  if (!text && !expandedAttachments.value.length && !hasPendingRef) {
    ElMessage.warning('请输入内容');
    return;
  }

  expandedAiInput.value = '';
  const snap = expandedAttachments.value;
  expandedAttachments.value = [];
  snap.forEach((item) => URL.revokeObjectURL(item.url));

  const sessionId = aiPanelRef.value?.getCurrentSessionId?.() ?? undefined;
  const uploadedAttachments: ChatMessageAttachment[] = [];
  const imgB64 = expandedImageBase64.value;
  expandedImageBase64.value = '';
  expandedImagePreview.value = '';
  for (const item of snap) {
    try {
      const res = await uploadChatFile(item.file, sessionId);
      const attType = item.file.type.startsWith('image/') ? 'image' : 'document';
      uploadedAttachments.push({
        name: item.name,
        processingName: res.processingName || res.fileName,
        processingObjectKey: res.processingObjectKey || res.objectKey,
        processingUrl: res.processingAccessUrl || res.accessUrl,
        url: res.accessUrl,
        type: attType,
        objectKey: res.objectKey,
      });
      // 图片直接转 base64 发送给多模态模型
      if (attType === 'image' && !imgB64) {
        try {
          const imgResp = await fetch(res.accessUrl);
          const blob = await imgResp.blob();
          const b64 = await new Promise<string>((resolve, reject) => {
            const reader = new FileReader();
            reader.onload = () => { const r = reader.result as string; resolve(r.split(',')[1] || r); };
            reader.onerror = reject;
            reader.readAsDataURL(blob);
          });
          expandedImageBase64.value = b64;
        } catch (e) { /* 转换失败，使用已有的 base64（如有） */ }
      }
    } catch (e: unknown) {
      const msg = e instanceof Error ? e.message : '附件上传失败';
      ElMessage.error(`${item.name}：${msg}`);
    }
  }
  // 粘贴或附件上传的图片 base64 都传给 sendAiMessageWithProject
  const finalImgB64 = imgB64 || expandedImageBase64.value;
  if (finalImgB64) { (window as any).__aipdPendingImage = finalImgB64; }
  expandedImageBase64.value = '';

  if (!text && !uploadedAttachments.length) return;

  const before = expandedChatMessages.value.length;
  expandedSending.value = true;
  try {
    await aiPanelRef.value?.submitExpandedInput?.(text, uploadedAttachments);
  } finally {
    expandedSending.value = false;
  }

  await nextTick();
  expandedComposerScrollRef.value?.scrollTo({
    top: expandedComposerScrollRef.value.scrollHeight,
    behavior: 'smooth',
  });

  const afterMessages = expandedChatMessages.value;
  const assistantMsg = afterMessages.length > before
    ? [...afterMessages].reverse().find((msg) => msg.role === 'assistant')
    : null;
  const structured = assistantMsg?.structuredResult ?? getLatestAssistantStructuredResult();
  const draft = extractProjectDraftFromStructured(structured);
  if (route.name === 'Dashboard' && structured && isStructuredProjectCreateReady(structured)) {
    if (!canAccessProjectCreateRoute(permissionStore)) {
      if (assistantMsg && !hasProjectCreateNoRouteAccessNotice(assistantMsg.content)) {
        assistantMsg.content = (assistantMsg.content || '') + formatProjectCreateNoRouteAccessNotice();
      }
      void aiPanelRef.value?.persistConversation?.();
      return;
    }
    await runProjectAutofillIntroSequence(shellStore, router, {
      structuredResult: structured as Record<string, unknown>,
      answerText: (assistantMsg?.content || '').trim(),
      conversationId: getPanelSessionId(),
    });
    return;
  }
  if (route.name === 'Dashboard' && hasEnoughProjectDraft(draft)) {
    shellStore.setPendingExpandedProjectDraft(draft);
    shellStore.setDashboardExpandedAiComposer(false);
  }
}
</script>

<template>
  <Teleport to="body">
    <div
      class="expanded-ai-overlay"
      aria-modal="true"
      role="dialog"
      :style="expandedShellRectStyle"
    >
      <div
        class="expanded-ai-root"
        :class="{ 'is-file-dragging': expandedDragging }"
        @dragenter.prevent="onExpandedDragEnter"
        @dragover.prevent="onExpandedDragOver"
        @dragleave.prevent="onExpandedDragLeave"
        @drop.prevent="onExpandedDrop"
      >
        <div v-if="expandedDragging" class="expanded-ai-drop-overlay" aria-hidden="true">
          松开上传文件
        </div>
    <div class="expanded-ai-top">
      <button
        type="button"
        class="expanded-ai-back-to-panel"
        title="回到侧栏对话"
        aria-label="回到侧栏对话"
        @click="exitExpandedAiComposer"
      >
        <span class="expanded-ai-back-to-panel-icon" aria-hidden="true"></span>
      </button>
      <template v-if="expandedEditingSessionTitle">
        <input
          ref="expandedSessionTitleInputRef"
          v-model="expandedEditingSessionTitleDraft"
          class="expanded-ai-session-title-input"
          type="text"
          @keydown.enter.prevent="confirmExpandedSessionTitleEdit"
          @keydown.esc.prevent="cancelExpandedSessionTitleEdit"
          @blur="confirmExpandedSessionTitleEdit"
        />
      </template>
      <div
        v-else
        class="expanded-ai-session-title expanded-ai-session-title--editable"
        title="双击修改标题"
        @dblclick="onExpandedSessionTitleDblClick"
      >
        {{ expandedSessionTitle }}
      </div>
      <div class="expanded-ai-header-right-cluster">
        <div class="expanded-ai-main-tabs">
          <button
            type="button"
            class="expanded-ai-tab-btn"
            :class="{ active: expandedMainTab === 'history', 'has-unread': expandedHistoryHasUnread }"
            @click="openHistoryAiPanel"
          >
            <span>历史会话</span>
            <span v-if="expandedHistoryHasUnread" class="expanded-ai-tab-unread-dot" aria-label="有未读会话" />
          </button>
          <button
            type="button"
            class="expanded-ai-tab-btn"
            :class="{ active: expandedMainTab === 'new' }"
            @click="openExpandedCurrentConversation"
          >
            当前会话
          </button>
        </div>
        <div class="expanded-ai-session-actions">
          <button
            type="button"
            class="expanded-ai-session-btn expanded-ai-session-btn--plus"
            title="新建会话（当前对话将保存到历史）"
            aria-label="新建会话"
            @click="onExpandedNewSession"
          >
            +
          </button>
          <button
            type="button"
            class="expanded-ai-session-btn expanded-ai-session-btn--minus"
            title="收起为悬浮按钮"
            aria-label="收起为悬浮按钮"
            @click="onExpandedCollapseToFloat"
          >
            −
          </button>
        </div>
      </div>
    </div>
    <div
      class="expanded-ai-body"
      :class="{ 'is-conversing': hasExpandedConversation && expandedMainTab === 'new' }"
    >
      <template v-if="expandedMainTab === 'history'">
        <div class="expanded-history-panel">
          <div class="expanded-history-hint">
            这里展示当前场景下的历史对话，点击可重新加载该轮对话继续沟通。
          </div>
          <div v-if="expandedHistoryLoading" class="expanded-history-loading">加载中…</div>
          <div v-else class="expanded-history-list">
            <button
              v-for="item in expandedHistoryList"
              :key="item.id"
              type="button"
              class="expanded-history-item"
              :class="{
                active: item.sessionId === expandedHighlightSessionId,
                'has-unread': isExpandedHistorySessionUnread(item),
                'is-running': isExpandedHistorySessionRunning(item.sessionId),
              }"
              @click="onExpandedHistoryRowClick(item)"
            >
              <div class="expanded-history-main">
                <div class="expanded-history-title">
                  <template v-if="expandedEditingHistoryId === item.id">
                    <input
                      ref="expandedHistoryTitleInputRef"
                      v-model="expandedEditingHistoryTitle"
                      class="expanded-history-title-input"
                      type="text"
                      @click.stop
                      @keydown.enter.prevent="confirmExpandedHistoryRename(item)"
                      @keydown.esc.prevent="cancelExpandedHistoryRename"
                      @blur="confirmExpandedHistoryRename(item)"
                    />
                  </template>
                  <template v-else>
                    <span class="expanded-history-title-text">{{ item.title || '对话' }}</span>
                    <span
                      v-if="isExpandedHistorySessionRunning(item.sessionId)"
                      class="expanded-session-running-dot"
                      title="任务进行中"
                      aria-hidden="true"
                    />
                    <span
                      v-if="isExpandedHistorySessionUnread(item)"
                      class="expanded-session-unread-dot"
                      title="有未读完成项"
                      aria-hidden="true"
                    />
                  </template>
                </div>
                <div class="expanded-history-meta">
                  {{ item.createdAt.slice(0, 16).replace('T', ' ') }}
                </div>
              </div>
              <div class="expanded-history-actions" @click.stop>
                <button
                  class="expanded-history-icon-btn"
                  type="button"
                  title="重命名"
                  @click="startExpandedHistoryRename(item)"
                >
                  ✎
                </button>
                <button
                  class="expanded-history-icon-btn"
                  type="button"
                  title="删除"
                  @click="confirmExpandedHistoryDelete(item)"
                >
                  ✕
                </button>
              </div>
            </button>
          </div>
          <div v-if="!expandedHistoryLoading && !expandedHistoryList.length" class="expanded-history-empty">
            暂无历史对话，发送消息后会自动为当前场景创建对话记录。
          </div>
        </div>
      </template>
      <template v-else>
      <template v-if="isDashboardRoute && !hasExpandedConversation">
        <div class="expanded-ai-hero">
          <h1 class="expanded-ai-title">Hi~ 我是一诺，今天有什么计划?</h1>
          <p class="expanded-ai-sub">倾听你的每一句话，让研发流程高效运转</p>
          <template v-if="canAccessProjectCreateRoute(permissionStore)">
            <p class="expanded-ai-guide-title">请输入以下相关信息，我来帮你创建项目：</p>
            <ul class="expanded-ai-guide-list">
              <li>这是什么类型的项目？（比如迭代性项目、全新型项目等）</li>
              <li>你希望文档包含哪些内容？（比如项目介绍、时间规划、成员分工、风险分析、SOR 文档等）</li>
              <li>有什么特别的要求或参考吗？</li>
            </ul>
          </template>
          <template v-else>
            <p class="expanded-ai-guide-title">你可以问我项目进度、风险与查找相关问题；创建项目需管理员/SE/项目经理角色。</p>
          </template>
        </div>
        <p class="expanded-ai-tags-hint">
          {{
            canAccessProjectCreateRoute(permissionStore)
              ? '请输入以下相关信息，我来帮你创建项目（可点击标签填入输入框）：'
              : '可点击下方标签快速开始（可点击标签填入输入框）：'
          }}
        </p>
        <div class="expanded-ai-tags">
          <button
            v-for="(tag, idx) in expandedQuickTags"
            :key="`eq-${idx}-${tag.label}`"
            type="button"
            class="expanded-tag"
            @click="fillExpandedPrompt(tag.text)"
          >
            {{ tag.label }}
          </button>
        </div>
      </template>
      <template v-else-if="!isDashboardRoute && !hasExpandedConversation">
        <div class="expanded-ai-hero expanded-ai-hero--compact">
          <h1 class="expanded-ai-title">一诺 · 璇玑管理平台助手</h1>
          <p class="expanded-ai-sub">在当前页面继续与一诺对话，完成后可点击左上角「&gt;」回到侧栏。</p>
        </div>
      </template>
      <div class="expanded-ai-chat" ref="expandedComposerScrollRef">
        <div
          v-for="msg in expandedChatMessages"
          :key="msg.id"
          :class="['expanded-msg', msg.role, msg.role === 'user' ? 'expanded-msg--user-card' : '']"
        >
              <!-- agent_complete navigation -->
            <div v-if="msg.role === 'assistant' && isAgentCompleteResult(getExpandedMessageStructuredResult(msg))" style="margin-top: 12px;">
              <button
                type="button"
                class="bom-generate-btn bom-generate-btn--primary"
                @click="handleAgentNavClick(msg)"
              >
                {{ getAgentCompleteNav(getExpandedMessageStructuredResult(msg))?.label || '查看结果' }} &rarr;
              </button>
            </div>

        <div v-if="msg.role === 'user'" class="expanded-msg-actions">
            <button
              type="button"
              class="expanded-msg-action-btn"
              title="复制"
              :disabled="!getExpandedUserCopyText(msg)"
              @click.stop="copyExpandedMessage(getExpandedUserCopyText(msg))"
            >
              <svg viewBox="0 0 24 24" aria-hidden="true"><path d="M16 1H6a2 2 0 0 0-2 2v12h2V3h10V1zm3 4H10a2 2 0 0 0-2 2v14h11a2 2 0 0 0 2-2V7a2 2 0 0 0-2-2zm0 16H10V7h9v14z"/></svg>
            </button>
            <button
              type="button"
              class="expanded-msg-action-btn"
              title="删除"
              @click.stop="removeExpandedUserMessage(msg.id)"
            >
              <svg viewBox="0 0 24 24" aria-hidden="true"><path d="M9 3h6l1 2h5v2H3V5h5l1-2zm1 6h2v9h-2V9zm4 0h2v9h-2V9zM6 7h12l-1 13a2 2 0 0 1-2 2H9a2 2 0 0 1-2-2L6 7z"/></svg>
            </button>
          </div>
          <template v-if="shouldShowExpandedInlineThought(msg)">
            <div class="ai-message-thought ai-message-thought--inline">
              <button type="button" class="ai-thought-toggle" @click="toggleExpandedThought(msg.id)">
                <span>思考步骤</span>
                <span>{{ expandedThoughtIds.has(msg.id) ? '收起' : '展开' }}</span>
              </button>
              <div v-show="expandedThoughtIds.has(msg.id)" class="ai-thought-preview ai-thought-preview-clip">
                <div v-for="step in getMessageThoughtSteps(msg)" :key="step.key" class="ai-thought-step">
                  <div class="ai-thought-step-head">
                    <strong>{{ step.title }}</strong>
                  </div>
                  <div class="ai-thought-step-body">{{ step.text }}</div>
                </div>
              </div>
            </div>
          </template>
          <div v-if="msg.role === 'user' && getAiPageContentUserRefFromMessage(msg)" class="expanded-msg-body expanded-msg-body--pc-ref">
              <button
                type="button"
                class="exp-pc-ref-chip"
                :aria-expanded="expandedPcRefOpenId === msg.id ? 'true' : 'false'"
                :title="expandedPcRefOpenId === msg.id ? '点击收起引用原文' : '点击定位页面原文'"
                @click.stop="onExpandedPageContentUserRefClick(msg)"
              >
                <span class="exp-pc-ref-prefix" aria-hidden="true">@</span>
                <span class="exp-pc-ref-label">{{ getAiPageContentUserRefFromMessage(msg)!.scopeLabel || '页面' }} · {{ getAiPageContentUserRefFromMessage(msg)!.fieldLabel }}</span>
                <span class="exp-pc-ref-snippet">「{{ truncatePcRefSnippetExpanded(getAiPageContentUserRefFromMessage(msg)!.quoted) }}」</span>
              </button>
              <div v-if="expandedPcRefOpenId === msg.id" class="exp-pc-ref-detail">
                <div class="exp-pc-ref-detail-label">引用原文</div>
                <div class="exp-pc-ref-detail-body">{{ getAiPageContentUserRefFromMessage(msg)!.quoted }}</div>
              </div>
              <div v-if="getAiPageContentUserRefFromMessage(msg)!.requirement" class="exp-pc-ref-req">
                {{ getAiPageContentUserRefFromMessage(msg)!.requirement }}
              </div>
              <div v-else class="exp-pc-ref-req is-placeholder">未填写说明，将按上下文润色该片段</div>
          </div>
          <div v-else-if="msg.role === 'user' && getProjectCreateUserRefFromMessage(msg)" class="expanded-msg-body expanded-msg-body--pc-ref">
              <button
                type="button"
                class="exp-pc-ref-chip"
                :aria-expanded="expandedPcRefOpenId === msg.id ? 'true' : 'false'"
                :title="expandedPcRefOpenId === msg.id ? '点击收起引用原文' : '点击查看完整引用原文'"
                @click.stop="toggleExpandedPcRef(msg.id)"
              >
                <span class="exp-pc-ref-prefix" aria-hidden="true">@</span>
                <span class="exp-pc-ref-label">创建项目 · {{ getProjectCreateUserRefFromMessage(msg)!.fieldLabel }}</span>
                <span class="exp-pc-ref-snippet">「{{ truncatePcRefSnippetExpanded(getProjectCreateUserRefFromMessage(msg)!.quoted) }}」</span>
              </button>
              <div v-if="expandedPcRefOpenId === msg.id" class="exp-pc-ref-detail">
                <div class="exp-pc-ref-detail-label">引用原文</div>
                <div class="exp-pc-ref-detail-body">{{ getProjectCreateUserRefFromMessage(msg)!.quoted }}</div>
              </div>
              <div v-if="getProjectCreateUserRefFromMessage(msg)!.requirement" class="exp-pc-ref-req">
                {{ getProjectCreateUserRefFromMessage(msg)!.requirement }}
              </div>
              <div v-else class="exp-pc-ref-req is-placeholder">未填写说明，将按上下文润色该片段</div>
          </div>
          <div
            v-else
            class="expanded-msg-body"
            :class="{ 'expanded-msg-body--selectable': msg.role === 'assistant' }"
            :data-ai-quote-scope="msg.role === 'assistant' ? 'AI回复' : undefined"
            :data-ai-quote-field="msg.role === 'assistant' ? 'ai-reply' : undefined"
            :data-ai-quote-label="msg.role === 'assistant' ? 'AI 回复' : undefined"
            :data-ai-quote-editable="msg.role === 'assistant' ? 'false' : undefined"
            :data-ai-quote-position="msg.role === 'assistant' ? 'AI 对话区助手回复正文' : undefined"
            v-html="msg.role === 'assistant' ? assistantExpandedBodyHtml(msg) : expandedMarkdown(msg.content || '')"
          />
          <div
            v-if="msg.role === 'assistant' && expandedIsWbsTaskReassignConfirm(msg)"
            class="expanded-pc-field-edit-footer"
          >
            <button
              type="button"
              class="expanded-pc-field-btn"
              :disabled="panelAiLoading"
              @click.stop="aiPanelRef?.declineWbsTaskReassign?.(msg)"
            >
              暂不重分配
            </button>
            <button
              type="button"
              class="expanded-pc-field-btn expanded-pc-field-btn--primary"
              :disabled="panelAiLoading"
              @click.stop="aiPanelRef?.acceptWbsTaskReassign?.(msg)"
            >
              开始重分配
            </button>
          </div>
          <div
            v-if="msg.role === 'assistant' && isPendingAiPageContentFieldEditMessage(msg)"
            class="expanded-pc-field-edit-footer"
          >
            <button
              type="button"
              class="expanded-pc-field-btn expanded-pc-field-btn--primary"
              @click.stop="aiPanelRef?.acceptPageContentFieldEdit?.(msg)"
            >
              采纳
            </button>
            <button
              type="button"
              class="expanded-pc-field-btn"
              @click.stop="aiPanelRef?.rejectPageContentFieldEdit?.(msg)"
            >
              拒绝
            </button>
          </div>
          <div
            v-else-if="msg.role === 'assistant' && aiPageContentFieldEditDoneLabel(msg)"
            class="expanded-pc-field-edit-done"
          >
            {{ aiPageContentFieldEditDoneLabel(msg) }}
          </div>
          <div
            v-else-if="msg.role === 'assistant' && isPendingProjectCreateFieldEditMessage(msg)"
            class="expanded-pc-field-edit-footer"
          >
            <button
              type="button"
              class="expanded-pc-field-btn expanded-pc-field-btn--primary"
              @click.stop="aiPanelRef?.acceptProjectCreateFieldEdit?.(msg)"
            >
              采纳
            </button>
            <button
              type="button"
              class="expanded-pc-field-btn"
              @click.stop="aiPanelRef?.rejectProjectCreateFieldEdit?.(msg)"
            >
              拒绝
            </button>
          </div>
          <div
            v-else-if="msg.role === 'assistant' && projectCreateFieldEditDoneLabel(msg)"
            class="expanded-pc-field-edit-done"
          >
            {{ projectCreateFieldEditDoneLabel(msg) }}
          </div>
          <div
            v-if="msg.role === 'assistant' && expandedWbsGenerateActionsVisible(msg)"
            class="expanded-pc-field-edit-footer"
          >
            <template v-if="expandedWbsGenerateStatus(msg) === 'completed'">
              <button
                type="button"
                class="expanded-pc-field-btn"
                :disabled="!canExpandedRollbackWbs(msg) || panelAiLoading || isExpandedWbsGenerateBusy(msg)"
                :title="expandedWbsRollbackTitle(msg)"
                @click.stop="aiPanelRef?.rollbackWbsFromMessage?.(msg)"
              >
                回退
              </button>
              <button
                type="button"
                class="expanded-pc-field-btn expanded-pc-field-btn--primary"
                :disabled="panelAiLoading || isExpandedWbsGenerateBusy(msg)"
                @click.stop="aiPanelRef?.regenerateWbsFromMessage?.(msg)"
              >
                重新生成
              </button>
            </template>
            <template v-else-if="expandedWbsGenerateStatus(msg) === 'failed'">
              <button
                type="button"
                class="expanded-pc-field-btn expanded-pc-field-btn--primary"
                :disabled="panelAiLoading || isExpandedWbsGenerateBusy(msg)"
                @click.stop="aiPanelRef?.regenerateWbsFromMessage?.(msg)"
              >
                重新生成
              </button>
            </template>
            <span v-else-if="expandedWbsGenerateStatus(msg) === 'reverted'" class="expanded-pc-field-edit-done">
              已回退本次 WBS 生成
            </span>
          </div>
          <div
            v-if="msg.role === 'assistant' && expandedSorRequirementActionsVisible(msg)"
            class="expanded-pc-field-edit-footer"
          >
            <button
              type="button"
              class="expanded-pc-field-btn"
              @click.stop="aiPanelRef?.rollbackSorRequirementFromMessage?.(msg)"
            >
              回退
            </button>
            <button
              type="button"
              class="expanded-pc-field-btn expanded-pc-field-btn--primary"
              @click.stop="aiPanelRef?.retrySorRequirementFromMessage?.(msg)"
            >
              重试
            </button>
          </div>
          <div
            v-if="msg.role === 'assistant' && expandedSessionActionMessageActionsVisible(msg)"
            class="expanded-pc-field-edit-footer"
          >
            <button
              type="button"
              class="expanded-pc-field-btn"
              title="回退到会话之前的状态"
              @click.stop="aiPanelRef?.rollbackSessionActionFromMessage?.(msg)"
            >
              回退
            </button>
            <button
              type="button"
              class="expanded-pc-field-btn expanded-pc-field-btn--primary"
              title="放弃上次结果并重新执行"
              @click.stop="aiPanelRef?.retrySessionActionFromMessage?.(msg)"
            >
              重试
            </button>
          </div>
        </div>
        <div
          v-if="panelIsThinking && panelLiveThoughtSteps.length"
          class="expanded-ai-thought-live ai-message-thought ai-message-thought--live"
        >
          <div class="ai-thought-toggle ai-thought-toggle--static">
            <span>思考步骤</span>
            <span>进行中</span>
          </div>
          <div class="ai-thought-preview ai-thought-preview-clip">
            <div
              v-for="step in panelLiveThoughtSteps"
              :key="step.key"
              class="ai-thought-step"
              :class="{ 'is-running': step.icon === '⏳' }"
            >
              <div class="ai-thought-step-head">
                <strong>{{ step.title }}</strong>
              </div>
              <div class="ai-thought-step-body">{{ step.text }}</div>
            </div>
          </div>
        </div>
        <div
          v-else-if="showExpandedThinkingOverlay"
          class="expanded-ai-waiting-strip"
        >
          <p class="expanded-ai-waiting-text">
            正在连接一诺
            <span class="dot">.</span><span class="dot">.</span><span class="dot">.</span>
          </p>
        </div>
        <div
          v-if="!expandedChatMessages.length && !showExpandedThinkingOverlay && !(panelIsThinking && panelLiveThoughtSteps.length)"
          class="expanded-msg-empty"
        >
          {{
            isDashboardRoute
              ? '在下方输入需求，一诺将协助你梳理与创建项目相关信息。'
              : '在下方输入需求，一诺将协助你继续当前场景下的对话。'
          }}
        </div>
      </div>
      <div
        class="expanded-ai-input-shell"
        :class="{
          'is-floating': hasExpandedConversation,
          'has-queue-strip': expandedActiveQueueTasks.length > 0,
        }"
      >
        <div v-if="panelPageContentEditPending" class="expanded-pc-ref-composer-row">
          <button
            type="button"
            class="exp-pc-ref-chip exp-pc-ref-chip--composer"
            :title="panelPageContentEditPending.quoted"
            @click="onExpandedPendingRefChipClick"
          >
            <span class="exp-pc-ref-prefix" aria-hidden="true">@</span>
            <span class="exp-pc-ref-label">{{ panelPageContentEditPending.scopeLabel }} · {{ panelPageContentEditPending.fieldLabel }}</span>
            <span class="exp-pc-ref-snippet">「{{ truncatePcRefSnippetExpanded(panelPageContentEditPending.quoted) }}」</span>
          </button>
          <button type="button" class="expanded-pc-ref-remove" title="移除引用" @click="clearExpandedPageContentRef">×</button>
        </div>
        <div v-if="expandedAttachments.length" class="expanded-ai-attachments">
          <div v-for="item in expandedAttachments" :key="item.id" class="expanded-ai-attachment">
            <span>{{ item.name }}</span>
            <button type="button" @click="removeExpandedAttachment(item.id)">×</button>
          </div>
        </div>
        <div v-if="expandedActiveQueueTasks.length" class="expanded-ai-task-queue-strip">
          <div
            v-for="task in expandedActiveQueueTasks"
            :key="task.id"
            class="expanded-ai-task-queue-item"
            :class="{
              'is-running': task.status === 'running',
              'is-queued': task.status === 'queued' && !taskQueue.isTaskQueueDeferred(task.id),
              'is-paused': task.status === 'paused' || taskQueue.isTaskQueueDeferred(task.id),
            }"
          >
            <span class="expanded-ai-task-queue-indicator" aria-hidden="true">
              <span v-if="task.status === 'running'" class="expanded-ai-task-queue-spinner" />
              <span v-else class="expanded-ai-task-queue-dot" />
            </span>
            <span class="expanded-ai-task-queue-label" :title="task.label">{{ task.label }}</span>
            <span class="expanded-ai-task-queue-status">{{
              task.status === 'running'
                ? '执行中'
                : task.status === 'paused' || taskQueue.isTaskQueueDeferred(task.id)
                  ? '已暂停'
                  : '排队中'
            }}</span>
            <button
              v-if="expandedQueueHeld && task.status === 'queued' && !expandedRunningTask && !expandedPausedTask"
              type="button"
              class="expanded-ai-task-queue-continue"
              title="继续执行排队任务"
              @click="onExpandedContinueScopeQueue"
            >
              继续
            </button>
          </div>
        </div>
        <div class="expanded-ai-input-inner">
          <textarea
            v-model="expandedAiInput"
            class="expanded-ai-textarea"
            placeholder="您需要我做什么呢？"
            rows="4"
            @keydown.enter.exact.prevent="sendExpandedAi"
            @paste="onExpandedPaste"
          />
          <div v-if="expandedImagePreview" class="expanded-image-preview">
            <img :src="expandedImagePreview" alt="截图预览" />
            <button type="button" class="expanded-image-remove" @click="expandedImageBase64 = ''; expandedImagePreview = ''" title="移除图片">✕</button>
          </div>
          <div class="expanded-ai-input-actions">
            <input
              ref="expandedFileInputRef"
              type="file"
              multiple
              :accept="CHAT_ATTACHMENT_ACCEPT"
              class="expanded-file-input"
              tabindex="-1"
              aria-hidden="true"
              @change="onExpandedFileSelect"
            />
            <button
              type="button"
              class="expanded-attach-btn"
              :title="CHAT_ATTACHMENT_HINT"
              :aria-label="CHAT_ATTACHMENT_HINT"
              @click="expandedFileInputRef?.click()"
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="14"
                height="14"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                stroke-width="2"
                stroke-linecap="round"
                stroke-linejoin="round"
                aria-hidden="true"
              >
                <path d="m21.44 11.05-9.19 9.19a6 6 0 0 1-8.49-8.49l9.19-9.19a4 4 0 0 1 5.66 5.66l-9.2 9.19a2 2 0 0 1-2.83-2.83l8.49-8.48" />
              </svg>
            </button>
            <button
              v-if="expandedStreaming || expandedShowTaskStop"
              type="button"
              class="expanded-send-btn expanded-send-btn--stop"
              title="停止当前任务"
              aria-label="停止当前任务"
              @click="onExpandedComposerStopClick"
            >
              <span class="expanded-send-stop-icon" aria-hidden="true" />
            </button>
            <button
              v-else
              type="button"
              class="expanded-send-btn"
              title="发送"
              aria-label="发送"
              :disabled="
                expandedSending ||
                panelAiLoading ||
                (!expandedAiInput.trim() && !expandedAttachments.length && !panelPageContentEditPending)
              "
              @click="sendExpandedAi"
            >
              <span v-if="expandedSending || panelAiLoading" class="expanded-loading-wave"><i></i><i></i><i></i></span>
              <svg
                v-else
                xmlns="http://www.w3.org/2000/svg"
                width="14"
                height="14"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                stroke-width="2"
                stroke-linecap="round"
                stroke-linejoin="round"
                aria-hidden="true"
              >
                <line x1="22" y1="2" x2="11" y2="13"/>
                <polygon points="22 2 15 22 11 13 2 9 22 2"/>
              </svg>
            </button>
          </div>
        </div>
      </div>
      </template>
    </div>
    <div
      v-if="isDashboardRoute && !hasExpandedConversation && expandedMainTab === 'new'"
      class="expanded-ai-footer"
    >
      <button type="button" class="btn btn-ghost" @click="exitExpandedAiComposer">返回项目列表</button>
      <button
        v-if="canAccessProjectCreateRoute(permissionStore)"
        type="button"
        class="btn btn-ghost"
        @click="openDashboardProjectForm"
      >使用表单创建项目</button>
    </div>
      </div>
    </div>
  </Teleport>
</template>

<style scoped>
.expanded-msg-body--selectable {
  user-select: text;
}

.expanded-ai-overlay {
  position: fixed;
  z-index: 4500;
  background: #f5f7fa;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  box-sizing: border-box;
  min-width: 0;
}

.expanded-ai-root {
  flex: 1;
  min-height: 0;
  min-width: 0;
  width: 100%;
  max-width: 100%;
  margin: 0 auto;
  padding: 12px 16px 20px;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  box-sizing: border-box;
  position: relative;
}

.expanded-ai-root.is-file-dragging {
  outline: 2px dashed #0052d9;
  outline-offset: -2px;
}

.expanded-ai-drop-overlay {
  position: absolute;
  inset: 0;
  z-index: 50;
  display: flex;
  align-items: center;
  justify-content: center;
  background: rgba(0, 82, 217, 0.08);
  border-radius: 8px;
  font-size: 14px;
  color: #0052d9;
  font-weight: 500;
  pointer-events: none;
}

.expanded-ai-top {
  display: flex;
  align-items: center;
  gap: 8px;
  flex-wrap: nowrap;
  min-width: 0;
  margin-bottom: 8px;
  flex-shrink: 0;
}

.expanded-ai-session-title {
  flex: 1;
  min-width: 0;
  font-size: 13px;
  font-weight: 500;
  color: #1f2329;
  line-height: 1.3;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.expanded-ai-session-title--editable {
  cursor: text;
}

.expanded-ai-session-title-input {
  flex: 1;
  min-width: 0;
  margin: 0;
  padding: 2px 6px;
  font-size: 13px;
  font-weight: 500;
  color: #1f2329;
  line-height: 1.3;
  border: 1px solid #3370ff;
  border-radius: 4px;
  outline: none;
  box-sizing: border-box;
}

.expanded-ai-header-right-cluster {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  flex-shrink: 0;
}

.expanded-ai-session-actions {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  flex-shrink: 0;
}

.expanded-ai-session-btn {
  width: 24px;
  height: 24px;
  border-radius: 50%;
  border: none;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  font-size: 16px;
  line-height: 1;
  font-weight: 500;
}

.expanded-ai-session-btn--plus {
  background: #e1e6ff;
  color: #0052d9;
}

.expanded-ai-session-btn--plus:hover {
  background: #0052d9;
  color: #fff;
}

.expanded-ai-session-btn--minus {
  background: #f3f4fb;
  color: #4e5969;
}

.expanded-ai-session-btn--minus:hover {
  background: #e1e6ff;
  color: #0052d9;
}

.expanded-ai-back-to-panel {
  flex-shrink: 0;
  width: 28px;
  height: 28px;
  padding: 0;
  border: none;
  background: transparent;
  color: #4e5969;
  font-size: 16px;
  line-height: 1;
  cursor: pointer;
  border-radius: 6px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
}

.expanded-ai-back-to-panel-icon {
  display: block;
  flex-shrink: 0;
  width: 20px;
  height: 20px;
  background-color: currentColor;
  -webkit-mask-image: url('../assets/codex-sidebar-collapsed.svg');
  -webkit-mask-repeat: no-repeat;
  -webkit-mask-position: center;
  -webkit-mask-size: contain;
  mask-image: url('../assets/codex-sidebar-collapsed.svg');
  mask-repeat: no-repeat;
  mask-position: center;
  mask-size: contain;
}

.expanded-ai-back-to-panel:hover {
  background: #f3f4fb;
  color: #0052d9;
}

.expanded-ai-main-tabs {
  display: inline-flex;
  flex-shrink: 0;
  border-radius: 999px;
  background: #f3f4fb;
  padding: 2px;
}

.expanded-ai-tab-btn {
  position: relative;
  border: none;
  background: transparent;
  padding: 4px 10px;
  border-radius: 999px;
  font-size: 12px;
  color: #4e5969;
  cursor: pointer;
}

.expanded-ai-tab-unread-dot {
  position: absolute;
  top: 2px;
  right: 2px;
  width: 6px;
  height: 6px;
  border-radius: 50%;
  background: #f53f3f;
}

.expanded-ai-tab-btn.active {
  background: #ffffff;
  color: #0052d9;
  box-shadow: 0 1px 3px rgba(15, 35, 52, 0.12);
}

.expanded-ai-hero--compact {
  text-align: center;
  margin-bottom: 16px;
}

.expanded-ai-title {
  font-size: 26px;
  font-weight: 600;
  color: #1f2329;
  margin: 0 0 12px;
  line-height: 1.35;
}

.expanded-ai-sub {
  font-size: 15px;
  color: #646a73;
  margin: 0 0 24px;
}

.expanded-ai-guide-title {
  font-size: 14px;
  color: #1f2329;
  margin: 0 0 10px;
  text-align: left;
}

.expanded-ai-guide-list {
  text-align: left;
  margin: 0;
  padding-left: 20px;
  color: #646a73;
  font-size: 14px;
  line-height: 1.75;
}

.expanded-ai-tags-hint {
  font-size: 13px;
  color: #8a8fa6;
  margin: 0 0 10px;
  text-align: left;
}

.expanded-ai-tags {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  margin-bottom: 18px;
  justify-content: flex-start;
}

.expanded-tag {
  padding: 4px 10px;
  border-radius: 4px;
  border: none;
  background: #e3edff;
  color: #0052d9;
  font-size: 12px;
  cursor: pointer;
}

.expanded-tag:hover {
  background: #d0e3ff;
}

.expanded-history-panel {
  flex: 1;
  min-height: 0;
  display: flex;
  flex-direction: column;
  overflow: hidden;
}

.expanded-history-hint {
  margin-bottom: 8px;
  padding: 8px 10px;
  border-radius: 4px;
  background: #f5f7ff;
  font-size: 12px;
  color: #4e5969;
  flex-shrink: 0;
}

.expanded-history-loading {
  font-size: 13px;
  color: #8a8fa6;
  padding: 12px 4px;
}

.expanded-history-list {
  flex: 1;
  min-height: 120px;
  overflow-y: auto;
  padding: 4px 0;
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.expanded-history-item {
  width: 100%;
  text-align: left;
  border: none;
  background: transparent;
  padding: 8px 10px;
  border-radius: 6px;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  box-sizing: border-box;
}

.expanded-history-item:hover {
  background: #f3f4fb;
}

.expanded-history-item.active {
  background: #e1e6ff;
}

.expanded-history-title {
  font-size: 13px;
  color: #1f2329;
  margin-bottom: 2px;
  display: flex;
  align-items: center;
  gap: 4px;
  min-width: 0;
}

.expanded-history-title-text {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  min-width: 0;
  flex: 1;
}

.expanded-session-running-dot {
  flex-shrink: 0;
  width: 6px;
  height: 6px;
  border-radius: 50%;
  background: #0052d9;
  animation: expanded-session-running-pulse 1.25s ease-in-out infinite;
}

.expanded-session-unread-dot {
  flex-shrink: 0;
  width: 7px;
  height: 7px;
  border-radius: 50%;
  background: #f53f3f;
}

.expanded-history-item.is-running .expanded-history-title-text {
  color: #1d4ed8;
}

@keyframes expanded-session-running-pulse {
  0% {
    transform: scale(0.92);
    box-shadow: 0 0 0 0 rgba(0, 82, 217, 0.45);
  }
  70% {
    transform: scale(1);
    box-shadow: 0 0 0 6px rgba(0, 82, 217, 0);
  }
  100% {
    transform: scale(0.92);
    box-shadow: 0 0 0 0 rgba(0, 82, 217, 0);
  }
}

.expanded-history-meta {
  font-size: 12px;
  color: #8a8fa6;
}

.expanded-history-actions {
  display: inline-flex;
  gap: 4px;
  flex-shrink: 0;
}

.expanded-history-icon-btn {
  width: 22px;
  height: 22px;
  border-radius: 4px;
  border: none;
  background: transparent;
  color: #8a8fa6;
  cursor: pointer;
  font-size: 12px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
}

.expanded-history-icon-btn:hover {
  background: #e5e7ff;
  color: #0052d9;
}

.expanded-history-title-input {
  width: 100%;
  border: 1px solid #d0d2da;
  border-radius: 4px;
  padding: 4px 6px;
  font-size: 13px;
  font-family: inherit;
  color: #1f2329;
  box-sizing: border-box;
}

.expanded-history-empty {
  font-size: 13px;
  color: #8a8fa6;
  padding: 16px 4px;
}

.expanded-ai-body {
  flex: 1;
  min-height: 0;
  min-width: 0;
  display: flex;
  flex-direction: column;
  overflow: hidden;
}

.expanded-ai-body.is-conversing {
  padding-top: 8px;
}

.expanded-ai-chat {
  flex: 1;
  min-height: 0;
  min-width: 0;
  overflow-y: auto;
  overflow-x: auto;
  margin-bottom: 0;
  padding: 12px 0;
  border-top: 1px solid #e5e6eb;
  border-bottom: 1px solid #e5e6eb;
  scrollbar-width: none;
}

.expanded-ai-chat::-webkit-scrollbar {
  width: 0;
  height: 0;
}

/* 等待回复：贴在会话末尾、与用户气泡区分（中性灰底，非浅蓝用户色） */
.expanded-ai-waiting-strip {
  margin-top: 4px;
  margin-bottom: 8px;
  padding: 10px 12px 10px 14px;
  border-radius: 8px;
  background: #f2f3f5;
  border: 1px dashed #d0d3db;
  border-left: 3px solid #86909c;
}

.expanded-ai-waiting-text {
  margin: 0;
  font-size: 13px;
  color: #646a73;
  line-height: 1.5;
}

.expanded-ai-waiting-strip .dot {
  animation: expanded-dot-blink 1s infinite alternate;
  opacity: 0.35;
}

.expanded-ai-waiting-strip .dot:nth-child(2) {
  animation-delay: 0.2s;
}

.expanded-ai-waiting-strip .dot:nth-child(3) {
  animation-delay: 0.4s;
}

@keyframes expanded-dot-blink {
  from {
    opacity: 0.2;
  }
  to {
    opacity: 1;
  }
}

.expanded-msg {
  margin-bottom: 14px;
  font-size: 12px;
  line-height: 1.6;
  max-width: 100%;
  overflow: hidden;
}

.expanded-msg-body--pc-ref {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.exp-pc-ref-chip {
  display: inline-flex;
  align-items: baseline;
  flex-wrap: wrap;
  gap: 4px 6px;
  max-width: 100%;
  padding: 0;
  margin: 0;
  border: none;
  background: none;
  font: inherit;
  font-size: 12px;
  text-align: left;
  cursor: pointer;
  color: #2563eb;
  text-decoration: underline;
  text-decoration-color: rgba(37, 99, 235, 0.35);
  text-underline-offset: 3px;
}

.exp-pc-ref-prefix {
  font-weight: 600;
  flex-shrink: 0;
}

.exp-pc-ref-label {
  font-weight: 500;
}

.exp-pc-ref-snippet {
  color: #646a73;
  text-decoration: none;
}

.expanded-pc-ref-composer-row {
  display: flex;
  align-items: flex-start;
  gap: 6px;
  padding: 0 4px 8px;
  flex-shrink: 0;
}

.expanded-pc-ref-remove {
  flex-shrink: 0;
  width: 22px;
  height: 22px;
  border: none;
  border-radius: 4px;
  background: transparent;
  color: #8a8fa6;
  font-size: 16px;
  line-height: 1;
  cursor: pointer;
}

.expanded-pc-ref-remove:hover {
  background: #f0f1f5;
  color: #1f2329;
}

.exp-pc-ref-chip--composer {
  cursor: pointer;
}

.exp-pc-ref-detail {
  width: 100%;
  padding: 8px 10px;
  border-radius: 8px;
  border: 1px solid #e5e7eb;
  background: #fafbfc;
}

.exp-pc-ref-detail-label {
  font-size: 11px;
  font-weight: 600;
  color: #646a73;
  margin-bottom: 6px;
}

.exp-pc-ref-detail-body {
  font-size: 12px;
  line-height: 1.55;
  color: #1f2329;
  white-space: pre-wrap;
  word-break: break-word;
}

.exp-pc-ref-req {
  font-size: 12px;
  line-height: 1.55;
  color: #1f2329;
  white-space: pre-wrap;
  word-break: break-word;
}

.exp-pc-ref-req.is-placeholder {
  color: #8a8fa6;
}

.expanded-pc-field-edit-footer {
  margin-top: 8px;
  padding-top: 8px;
  border-top: 1px solid #e8ecf2;
  display: flex;
  gap: 8px;
  flex-wrap: wrap;
}

.expanded-pc-field-btn {
  padding: 4px 14px;
  font-size: 12px;
  border-radius: 6px;
  border: 1px solid #dcdfe6;
  background: #fff;
  cursor: pointer;
  color: #606266;
}

.expanded-pc-field-btn--primary {
  border-color: #0052d9;
  color: #0052d9;
}

.expanded-pc-field-btn--primary:hover {
  background: #e8f0ff;
}

.expanded-pc-field-edit-done {
  margin-top: 8px;
  font-size: 12px;
  color: #909399;
}

.expanded-msg-body {
  min-width: 0;
  max-width: 100%;
  overflow-wrap: anywhere;
  word-break: break-word;
}

.expanded-msg.user .expanded-msg-body {
  background: #e8f4ff;
  border-radius: 10px;
  padding: 10px 14px;
  color: #1f2329;
}

.expanded-msg--user-card {
  position: relative;
  margin-left: auto;
  max-width: min(480px, 92%);
}

.expanded-msg--user-card .expanded-msg-body {
  padding: 10px 40px 28px 14px;
}

.expanded-msg-actions {
  position: absolute;
  right: 8px;
  bottom: 8px;
  display: flex;
  gap: 4px;
  opacity: 0;
  transition: opacity 0.15s ease;
  z-index: 1;
}

.expanded-msg--user-card:hover .expanded-msg-actions {
  opacity: 1;
}

.expanded-msg-action-btn {
  width: 22px;
  height: 22px;
  border: none;
  border-radius: 6px;
  background: rgba(255, 255, 255, 0.95);
  color: #4e5969;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.08);
}

.expanded-msg-action-btn svg {
  width: 13px;
  height: 13px;
  fill: currentColor;
}

.expanded-msg-action-btn:hover:not(:disabled) {
  color: #0052d9;
}

.expanded-msg-action-btn:disabled {
  opacity: 0.45;
  cursor: default;
}

.expanded-msg.assistant .expanded-msg-body {
  padding: 4px 0;
  color: #1f2329;
}

.expanded-msg-body :deep(pre),
.expanded-msg-body :deep(code),
.expanded-msg-body :deep(table) {
  max-width: 100%;
}

.expanded-msg-body :deep(pre) {
  overflow-x: auto;
}

.expanded-msg-body :deep(img) {
  max-width: 100%;
  height: auto;
}

.expanded-msg-empty {
  text-align: center;
  color: #8a8fa6;
  font-size: 14px;
  padding: 24px 12px;
}

.ai-message-thought {
  margin: 6px 0 8px;
  border: 1px solid #ededed;
  background: #fafafa;
  border-radius: 10px;
  overflow: hidden;
}

.ai-message-thought--inline {
  margin-bottom: 8px;
}

.expanded-ai-thought-live,
.ai-message-thought--live {
  margin: 10px 0 14px;
  border-style: dashed;
}

.ai-thought-toggle--static {
  cursor: default;
}

.ai-thought-step.is-running .ai-thought-step-head strong {
  color: #409eff;
}

.ai-thought-toggle {
  width: 100%;
  border: none;
  background: transparent;
  padding: 8px 10px;
  display: flex;
  justify-content: space-between;
  align-items: center;
  color: #969696;
  font-size: 12px;
  cursor: pointer;
}

.ai-thought-preview-clip {
  max-height: 144px;
  overflow-y: auto;
  overflow-x: hidden;
  display: grid;
  gap: 6px;
}

.ai-thought-preview {
  padding: 0 10px 10px;
}

.ai-thought-step {
  border-left: 2px solid #e7e7e7;
  padding: 4px 0 4px 8px;
}

.ai-thought-step-head {
  margin-bottom: 2px;
  color: #969696;
}

.ai-thought-step-body {
  color: #969696;
  font-size: 12px;
  line-height: 1.6;
  white-space: pre-wrap;
}

.expanded-ai-input-shell {
  background: #fafbfc;
  border: 1px solid #e5e6eb;
  border-radius: 12px;
  padding: 12px 12px 10px;
  box-shadow: 0 4px 14px rgba(15, 35, 52, 0.06);
  margin-top: auto;
  position: sticky;
  bottom: 16px;
  z-index: 3;
  align-self: stretch;
  min-width: 0;
  max-width: 100%;
  box-sizing: border-box;
}

.expanded-ai-input-shell.has-queue-strip {
  padding-top: 0;
  overflow: hidden;
}

.expanded-ai-input-shell.is-dragging {
  border-color: #0052d9;
  background: #e8f0fe;
}

.expanded-ai-input-shell.is-floating {
  position: sticky;
  bottom: 16px;
  z-index: 3;
}

.expanded-ai-drop-hint {
  position: absolute;
  inset: 0;
  z-index: 2;
  display: flex;
  align-items: center;
  justify-content: center;
  background: rgba(0, 82, 217, 0.08);
  border-radius: 12px;
  font-size: 14px;
  color: #0052d9;
  font-weight: 500;
  pointer-events: none;
}

.expanded-ai-task-queue-strip {
  display: flex;
  flex-direction: column;
  gap: 4px;
  margin: 0 -12px 8px;
  padding: 8px 12px;
  border-radius: 0;
  border: none;
  border-bottom: 1px solid #e5e6eb;
  background: #f7f8fc;
  max-height: 132px;
  overflow-y: auto;
}

.expanded-ai-task-queue-item {
  display: flex;
  align-items: center;
  gap: 8px;
  min-width: 0;
  padding: 4px 6px;
  border-radius: 6px;
  font-size: 12px;
  line-height: 1.4;
  color: #4e5969;
}

.expanded-ai-task-queue-item.is-running {
  background: #eef3ff;
  color: #1d4ed8;
}

.expanded-ai-task-queue-item.is-queued {
  opacity: 0.88;
}

.expanded-ai-task-queue-item.is-paused {
  background: #fff7e6;
  color: #d48806;
}

.expanded-ai-task-queue-indicator {
  flex-shrink: 0;
  width: 12px;
  height: 12px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
}

.expanded-ai-task-queue-spinner {
  width: 10px;
  height: 10px;
  border: 1.5px solid #c9d7ff;
  border-top-color: #0052d9;
  border-radius: 50%;
  animation: expanded-ai-task-queue-spin 0.75s linear infinite;
}

.expanded-ai-task-queue-dot {
  width: 6px;
  height: 6px;
  border-radius: 50%;
  background: #c9cdd4;
}

.expanded-ai-task-queue-label {
  flex: 1;
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.expanded-ai-task-queue-status {
  flex-shrink: 0;
  font-size: 11px;
  color: #86909c;
}

.expanded-ai-task-queue-item.is-running .expanded-ai-task-queue-status {
  color: #4e7cff;
}

.expanded-ai-task-queue-item.is-paused .expanded-ai-task-queue-status {
  color: #d48806;
}

.expanded-ai-task-queue-continue {
  flex-shrink: 0;
  margin-left: 4px;
  padding: 0 8px;
  height: 22px;
  border: 1px solid #b7d1ff;
  border-radius: 4px;
  background: #fff;
  color: #0052d9;
  font-size: 11px;
  cursor: pointer;
}

.expanded-ai-task-queue-continue:hover {
  background: #eef3ff;
}

.expanded-task-action-btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 44px;
  height: 28px;
  padding: 0 10px;
  border: 1px solid #e5e6eb;
  border-radius: 6px;
  background: #fff;
  color: #4e5969;
  font-size: 12px;
  cursor: pointer;
}

.expanded-task-action-btn--primary {
  border-color: #b7d1ff;
  color: #0052d9;
  background: #eef3ff;
}

.expanded-send-btn--pause {
  font-size: 12px;
  letter-spacing: -1px;
}

.expanded-ai-task-queue-stop {
  flex-shrink: 0;
  margin-left: 4px;
  padding: 0 8px;
  height: 22px;
  border: 1px solid #f5c2c7;
  border-radius: 4px;
  background: #fff;
  color: #cf1322;
  font-size: 11px;
  line-height: 20px;
  cursor: pointer;
}

.expanded-ai-task-queue-stop:hover {
  background: #fff1f0;
  border-color: #ffa39e;
}

@keyframes expanded-ai-task-queue-spin {
  to {
    transform: rotate(360deg);
  }
}

.expanded-ai-input-inner {
  position: relative;
  width: 100%;
  min-height: 120px;
}

.expanded-image-preview {
  position: relative;
  display: inline-block;
  margin: 8px 0;
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  overflow: hidden;
}
.expanded-image-preview img {
  max-height: 120px;
  max-width: 100%;
  display: block;
}
.expanded-image-remove {
  position: absolute;
  top: 4px;
  right: 4px;
  width: 20px;
  height: 20px;
  border: none;
  border-radius: 50%;
  background: rgba(0,0,0,0.5);
  color: #fff;
  font-size: 12px;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
}
.expanded-ai-textarea {
  width: 100%;
  border: none;
  resize: vertical;
  min-height: 120px;
  font-size: 14px;
  line-height: 1.5;
  font-family: inherit;
  outline: none;
  color: #1f2329;
  background: transparent;
  display: block;
  box-sizing: border-box;
  padding: 8px 44px 10px 8px;
}

.expanded-ai-input-actions {
  position: absolute;
  right: 4px;
  bottom: 4px;
  display: flex;
  align-items: center;
  gap: 4px;
  z-index: 1;
}

.expanded-file-input {
  position: absolute;
  width: 0;
  height: 0;
  opacity: 0;
  pointer-events: none;
  overflow: hidden;
}

.expanded-attach-btn {
  width: 28px;
  height: 28px;
  border-radius: 6px;
  border: none;
  background: #e5e6eb;
  color: #4e5969;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  padding: 0;
}

.expanded-attach-btn:hover {
  background: #d0d2da;
  color: #1f2329;
}

.expanded-attach-btn svg {
  width: 14px;
  height: 14px;
  display: block;
  flex-shrink: 0;
}

.expanded-send-btn {
  width: 28px;
  height: 28px;
  border-radius: 6px;
  border: none;
  background: #0052d9;
  color: #ffffff;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  padding: 0;
}

.expanded-send-btn--stop {
  background: #cf1322;
}

.expanded-send-btn--stop:hover:not(:disabled) {
  background: #a8071a;
}

.expanded-send-stop-icon {
  display: block;
  width: 10px;
  height: 10px;
  border-radius: 1px;
  background: #ffffff;
}

.expanded-send-btn:hover:not(:disabled) {
  background: #0047c5;
}

.expanded-send-btn:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.expanded-send-btn svg {
  display: block;
}

.expanded-loading-wave {
  display: inline-flex;
  gap: 2px;
  align-items: center;
}

.expanded-loading-wave i {
  width: 3px;
  height: 10px;
  border-radius: 999px;
  background: #fff;
  opacity: 0.7;
  animation: expanded-wave 0.9s infinite ease-in-out;
}

.expanded-loading-wave i:nth-child(2) {
  animation-delay: 0.12s;
}

.expanded-loading-wave i:nth-child(3) {
  animation-delay: 0.24s;
}

@keyframes expanded-wave {
  0%,
  80%,
  100% {
    transform: scaleY(0.45);
    opacity: 0.5;
  }
  40% {
    transform: scaleY(1);
    opacity: 1;
  }
}

.expanded-ai-footer {
  display: flex;
  flex-wrap: wrap;
  gap: 12px;
  justify-content: center;
  margin-top: 20px;
}

.btn-ghost {
  background: transparent;
  border: 1px solid #e5e6eb;
  color: #646a73;
  padding: 8px 14px;
  border-radius: 8px;
  cursor: pointer;
  font-size: 14px;
}

.btn-ghost:hover {
  background: #f2f3f5;
  color: #1f2329;
}

.expanded-ai-root,
.expanded-ai-root * {
  scrollbar-width: none;
}

.expanded-ai-root::-webkit-scrollbar,
.expanded-ai-root *::-webkit-scrollbar {
  width: 0;
  height: 0;
}

@media (max-width: 992px) {
  /* 与 Layout.vue 中 .app-shell-body 窄屏左边距一致（收起轨宽 55） */
  .expanded-ai-overlay {
    left: 55px !important;
  }

  .expanded-ai-root {
    padding: 10px 12px 16px;
  }
}
</style>
